/**
 * F1 Central — design tokens
 * -------------------------------------------------------------------------
 * JavaScript mirror of the Tailwind theme. Use these constants when colour
 * values must reach JS (inline styles, canvas drawing, chart libraries) —
 * everything else should stay in Tailwind classes.
 *
 * Keep this file aligned with `tailwind.config.js`. The duplication is
 * intentional: Tailwind values live in CSS at build time, but charting and
 * animation libraries need runtime access.
 */

export const palette = {
  bg: {
    base: '#050505',
    elevated: '#0c0c0e',
    card: '#141417',
    card2: '#1a1a1f',
    card3: '#22222a',
  },
  stroke: {
    subtle: '#1f1f24',
    default: '#2a2a32',
    strong: '#3a3a44',
  },
  ink: {
    primary: '#f4f4f5',
    secondary: '#a1a1aa',
    tertiary: '#71717a',
    muted: '#52525b',
  },
  f1: {
    red: '#e10600',
    redBright: '#ff1801',
    redDeep: '#a30400',
  },
  signal: {
    green: '#22c55e',
    yellow: '#f5c518',
    amber: '#f59e0b',
    blue: '#3b82f6',
    purple: '#a855f7',
  },
};

/**
 * Team livery palette — used for driver / constructor accents.
 *
 * Aliases cover Jolpica-F1's constructor naming, which differs from the
 * shorthand used elsewhere in the app (e.g. "Red Bull" not "Red Bull Racing",
 * "Haas F1 Team" not "Haas").
 *
 * HISTORICAL TEAMS ARE ENUMERATED (Session 7). An earlier comment claimed the
 * grey fallback for pre-2018 teams was intentional. In practice it made the
 * leaderboard unreadable: 241 of 928 standings rows across 2000–2026 fell back
 * to the same neutral grey, and in 2000 that was 15 of 23 drivers plus 7 of 11
 * constructors — roughly two thirds of the table rendered as identical bars,
 * which is what "the older years look scrambled" turned out to mean.
 *
 * Historical values are recognisable approximations of each team's best-known
 * livery, chosen so that teams are distinguishable WITHIN any single season
 * (that is the only comparison a user actually makes). They are not exact
 * brand hexes and don't need to be.
 */
export const teamColors = {
  'Red Bull Racing': '#1E3A8A',
  'Red Bull': '#1E3A8A',
  'Ferrari': '#DC0000',
  'Scuderia Ferrari': '#DC0000',
  'McLaren': '#FF8000',
  'Mercedes': '#00D2BE',
  'Aston Martin': '#229971',
  'Williams': '#64C4FF',
  'Audi': '#52E252',
  'Kick Sauber': '#52E252',
  'Sauber': '#52E252',
  'Racing Bulls': '#6692FF',
  'RB F1 Team': '#6692FF',
  'AlphaTauri': '#6692FF',
  'Toro Rosso': '#6692FF',
  'Haas': '#B6BABD',
  'Haas F1 Team': '#B6BABD',
  'Alpine': '#0093CC',
  'Alpine F1 Team': '#0093CC',
  'Renault': '#0093CC',
  'Cadillac': '#FFFFFF',
  // Jolpica returns "Cadillac F1 Team", not "Cadillac" — without this alias a
  // current-grid 2026 constructor rendered with the neutral grey fallback.
  'Cadillac F1 Team': '#FFFFFF',
  // 2019–2023 Sauber branding; recent enough that grey looks like a bug.
  'Alfa Romeo': '#900000',
  'Alfa Romeo Racing': '#900000',
  'Racing Point': '#F596C8',
  'Force India': '#F596C8',

  /* --- Historical constructors (2000–2016) ------------------------------- */
  'Benetton': '#0057B8',        // Playlife blue, 2000–01
  'BAR': '#8E1B3F',             // Lucky Strike white/red — wine tone, separates from Ferrari
  'Jordan': '#FFD800',          // Jordan yellow
  'Arrows': '#C05600',          // Orange-sponsor livery, deepened vs McLaren's orange
  'Jaguar': '#0B5A3C',          // British racing green
  'Minardi': '#3F4C6B',         // slate — separated from BAR's wine (they coexist 2000–05)
  'Prost': '#153D7A',           // deep navy
  'Toyota': '#CFD2D4',          // predominantly white cars
  'Honda': '#1E9E8A',           // "earth car" teal — Williams owns light blue in 2006–08
  'BMW Sauber': '#0066B1',      // BMW blue, lifted clear of Red Bull's navy
  'Super Aguri': '#D81E3F',
  'MF1': '#B32D2E',             // Midland
  'Spyker MF1': '#C25E00',      // same lineage as Spyker below
  'Spyker': '#C25E00',          // burnt orange — McLaren owns bright orange
  'Brawn': '#B8D000',           // the 2009 lime — unmistakable
  'Lotus': '#00594F',           // Team Lotus green, 2010–11
  'HRT': '#6E7276',             // grey/red — kept neutral so it can't clash with Marussia
  'Virgin': '#E8114B',
  'Lotus F1': '#D4AF37',        // Enstone's black & gold, 2012–15
  'Caterham': '#0B573F',
  'Marussia': '#B01B2E',
  'Manor Marussia': '#D1122E',
};

export const motion = {
  // Easing curves named after their use case so call sites read clearly
  easing: {
    standard: 'cubic-bezier(0.4, 0, 0.2, 1)',
    decelerate: 'cubic-bezier(0, 0, 0.2, 1)',
    accelerate: 'cubic-bezier(0.4, 0, 1, 1)',
    spring: 'cubic-bezier(0.16, 1, 0.3, 1)',
  },
  duration: {
    instant: 80,
    fast: 160,
    standard: 240,
    slow: 320,
    deliberate: 480,
  },
};
